import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LineChart, Cpu, Award, BookOpen, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LandingNavbar from "../components/LandingNavbar";

export default function Contacto() {
  const navigate = useNavigate();

  // Hospital Status brand colors
  const hospitalColors = [
    "#122056", // Base: med-navy
    "#3577D5", // Layer 1: med-accent (blue)
    "#10B981", // Layer 2: emerald-500
    "#6366F1", // Layer 3: indigo-500
    "#0F172A", // Layer 4: slate-900
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-med-accent selection:text-white">
      {/* Floating Navbar — same as landing */}
      <LandingNavbar />

      {/* Main Content */}
      <div className="pt-24">
        <ContactSectionWithShader colors={hospitalColors} />
      </div>
    </div>
  );
}

type ContactSectionProps = {
  colors?: string[];
};

export function ContactSectionWithShader({ colors }: ContactSectionProps) {
  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    // Logic to handle form submission can go here
  };

  return (
    <div className="w-full">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-10 md:px-8 lg:grid-cols-2 lg:py-16">
        {/* Left - Shader with Testimonials */}
        <div className="relative order-last h-[500px] md:h-[600px] overflow-hidden rounded-[2.5rem] md:order-first lg:h-auto shadow-2xl">
          <ShaderBackground colors={colors} />
          <div className="absolute inset-0 z-10 flex items-center justify-center p-8 bg-black/10">
            <RotatingTestimonials />
          </div>
        </div>

        {/* Right - Contact Form */}
        <div className="flex items-center justify-center lg:pl-10">
          <div className="w-full max-w-lg rounded-3xl px-4 md:px-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-med-light/30 border border-slate-200 mb-4">
                <span className="text-[11px] font-medium text-med-navy uppercase tracking-[0.08em] font-heading">
                  Contacto
                </span>
              </div>
              <h1 className="mt-4 text-3xl md:text-5xl leading-[1.15] font-bold text-med-navy font-heading tracking-[-0.025em]">
                Hablemos de <span className="text-med-accent">tu hospital</span>
              </h1>
              <p className="mt-4 text-med-navy/50 font-normal leading-[1.6]">
                Déjanos tus datos y un especialista de nuestro equipo se comunicará contigo para entender la operación de tu hospital y diseñar una propuesta a la medida.
              </p>
            </div>

            <div className="py-10">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-med-navy mb-2">
                    Nombre Completo
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    placeholder="Dr. Juan Pérez"
                    className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm focus:border-med-accent focus:ring-2 focus:ring-med-accent/20 outline-none transition-all placeholder:text-slate-400"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-med-navy mb-2">
                    Correo Electrónico
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    placeholder="director@hospital.com"
                    className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm focus:border-med-accent focus:ring-2 focus:ring-med-accent/20 outline-none transition-all placeholder:text-slate-400"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-med-navy mb-2">
                    Institución u Hospital
                  </label>
                  <input
                    id="company"
                    type="text"
                    required
                    placeholder="Centro Médico ABC"
                    className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm focus:border-med-accent focus:ring-2 focus:ring-med-accent/20 outline-none transition-all placeholder:text-slate-400"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-med-navy mb-2">
                    Mensaje
                  </label>
                  <textarea
                    rows={4}
                    id="message"
                    required
                    placeholder="¿Cuántos quirófanos manejan? ¿Qué problema quieres resolver primero?"
                    className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm focus:border-med-accent focus:ring-2 focus:ring-med-accent/20 outline-none transition-all placeholder:text-slate-400 resize-none font-sans"
                  />
                </div>

                <div className="pt-4">
                  <button className="relative w-full overflow-hidden rounded-xl bg-med-navy px-8 py-4 font-medium text-white transition-all duration-300 hover:bg-med-accent hover:shadow-[0_0_20px_rgba(53,119,213,0.4)] hover:-translate-y-1 group">
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      Enviar Mensaje
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// User-provided phrases
const testimonials = [
  {
    quote: "Lo que no se mide, no se puede mejorar.",
    name: "Peter Drucker",
    designation: "Filósofo de la Administración",
    icon: <LineChart className="w-6 h-6 text-emerald-400" />,
  },
  {
    quote: "La tecnología es solo una herramienta. La gente es la que hace que las cosas sucedan.",
    name: "Steve Jobs",
    designation: "Fundador de Apple",
    icon: <Cpu className="w-6 h-6 text-med-accent" />,
  },
  {
    quote: "La excelencia no es un acto, sino un hábito.",
    name: "Aristóteles",
    designation: "Filósofo Griego",
    icon: <Award className="w-6 h-6 text-amber-400" />,
  },
  {
    quote: "Cada paciente es un libro que debemos aprender a leer.",
    name: "Sir William Osler",
    designation: "Padre de la Medicina Moderna",
    icon: <BookOpen className="w-6 h-6 text-emerald-300" />,
  },
  {
    quote: "El tiempo es el recurso más escaso; si no se gestiona, nada más se puede gestionar.",
    name: "Peter Drucker",
    designation: "Filósofo de la Administración",
    icon: <Clock className="w-6 h-6 text-indigo-300" />,
  },
];

function RotatingTestimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative flex h-full w-full max-w-md items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, filter: "blur(10px)", scale: 0.95, y: 10 }}
          animate={{ opacity: 1, filter: "blur(0px)", scale: 1, y: 0 }}
          exit={{ opacity: 0, filter: "blur(10px)", scale: 0.95, y: -10 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full"
        >
          <TestimonialCard testimonial={testimonials[currentIndex]} />
        </motion.div>
      </AnimatePresence>

      {/* Indicators */}
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? "w-6 bg-white"
                : "bg-white/40 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function TestimonialCard({
  testimonial,
}: {
  testimonial: (typeof testimonials)[0];
}) {
  return (
    <div className="rounded-3xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-xl">
      <div className="mb-6 flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 text-2xl shadow-inner">
        {testimonial.icon}
      </div>
      <p className="text-2xl leading-snug font-medium text-white font-heading tracking-[-0.015em]">
        "{testimonial.quote}"
      </p>
      <div className="mt-8 flex items-center gap-4">
        <div>
          <p className="font-medium text-white text-lg">{testimonial.name}</p>
          <p className="text-sm text-white/70 font-medium tracking-wide">{testimonial.designation}</p>
        </div>
      </div>
    </div>
  );
}

function resolveCssColorToRGB(color: string): [number, number, number] {
  if (typeof document === "undefined") {
    return [128, 128, 128];
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d");
  if (!ctx) return [128, 128, 128];

  const el = document.createElement("div");
  el.style.color = color;
  document.body.appendChild(el);
  const computed = getComputedStyle(el).color;
  document.body.removeChild(el);

  ctx.fillStyle = computed;
  ctx.fillRect(0, 0, 1, 1);
  const data = ctx.getImageData(0, 0, 1, 1).data;

  return [data[0], data[1], data[2]];
}

const vertexShaderSource = `
  attribute vec2 a_position;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision highp float;

  uniform float u_time;
  uniform vec2 u_resolution;
  uniform vec3 u_colors[5];

  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }

  vec3 blendNormal(vec3 base, vec3 blend, float opacity) {
    return blend * opacity + base * (1.0 - opacity);
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution;
    float aspect = u_resolution.x / u_resolution.y;
    float time = u_time * 0.15;

    vec2 nCoord = vec2(uv.x * aspect, uv.y) * 0.4;

    vec3 color = u_colors[0]; // Base color

    float n1 = snoise(vec3(nCoord.x * 1.3 + time * 0.5, nCoord.y * 1.6, time * 0.3 + 3.0));
    n1 = smoothstep(0.15, 0.7, n1 * 0.5 + 0.5);
    color = blendNormal(color, u_colors[1], pow(n1, 3.5));

    float n2 = snoise(vec3(nCoord.x * 1.5 + time * 0.4, nCoord.y * 1.8, time * 0.35 + 12.0));
    n2 = smoothstep(0.18, 0.75, n2 * 0.5 + 0.5);
    color = blendNormal(color, u_colors[2], pow(n2, 3.5));

    float n3 = snoise(vec3(nCoord.x * 1.1 - time * 0.35, nCoord.y * 1.4, time * 0.25 + 24.0));
    n3 = smoothstep(0.20, 0.80, n3 * 0.5 + 0.5);
    color = blendNormal(color, u_colors[3], pow(n3, 4.0));

    float n4 = snoise(vec3(nCoord.x * 0.9 + time * 0.2, nCoord.y * 1.2, time * 0.15 + 36.0));
    n4 = smoothstep(0.25, 0.85, n4 * 0.5 + 0.5);
    color = blendNormal(color, u_colors[4], pow(n4, 4.0));

    float vignette = smoothstep(1.2, 0.4, length(uv - vec2(0.5)));
    color *= vignette * 0.85 + 0.15;

    gl_FragColor = vec4(color, 1.0);
  }
`;

type ShaderBackgroundProps = {
  colors?: string[];
};

function ShaderBackground({ colors = [] }: ShaderBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  const createShader = useCallback(
    (gl: WebGLRenderingContext, type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("Shader compile error:", gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    },
    [],
  );

  const parseColors = useCallback(() => {
    const resolved = [...colors];
    while (resolved.length < 5) {
      resolved.push(resolved[resolved.length - 1] ?? "#122056"); // Fallback to med-navy
    }
    const rgb = resolved.slice(0, 5).map((c) => {
      const [r, g, b] = resolveCssColorToRGB(c);
      return [r / 255, g / 255, b / 255] as [number, number, number];
    });
    return rgb.flat();
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { antialias: false });
    if (!gl) return;

    const vs = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fs = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vs || !fs) return;

    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program link error:", gl.getProgramInfoLog(program));
      return;
    }
    gl.useProgram(program);

    const vertices = new Float32Array([
      -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
    ]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const aPosition = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

    const uTime = gl.getUniformLocation(program, "u_time");
    const uResolution = gl.getUniformLocation(program, "u_resolution");
    const uColors = gl.getUniformLocation(program, "u_colors");

    const colorArray = new Float32Array(parseColors());
    gl.uniform3fv(uColors, colorArray);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);

    const startTime = performance.now();
    const render = () => {
      const elapsed = (performance.now() - startTime) / 1000;
      gl.uniform1f(uTime, elapsed);
      gl.uniform2f(uResolution, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [createShader, parseColors]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        style={{ display: "block" }}
      />
      {/* Noise overlay for texture */}
      <svg className="pointer-events-none absolute inset-0 z-[5] h-full w-full opacity-[0.25]">
        <filter id="contactShaderNoise">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.8"
            numOctaves="4"
            stitchTiles="stitch"
          />
        </filter>
        <rect width="100%" height="100%" filter="url(#contactShaderNoise)" />
      </svg>
    </>
  );
}
